import { ReactNode } from "react";
import { Alert, Badge, Code, Group, Stack, Table, Text } from "@mantine/core";
import {
  DAY_ORDER,
  dayLabel,
  normaliseOperatingHours,
} from "@/lib/operatingHours";
import { storeDaySchedule, type DaySchedule } from "@/lib/schedule";
import { formatMinutes } from "@/lib/timezone";
import { fmt } from "@/lib/format";
import type { LebronStore } from "@/lib/queries";

interface OperatingHoursTableProps {
  store: LebronStore;
}

/**
 * Every weekday for a store, resolved the same way the timeline resolves it.
 *
 * Both go through `storeDaySchedule`, so the two views cannot disagree: a day
 * that `operating_hours` omits shows the store's default window, flagged as
 * such, rather than being left out — which previously read as "nothing known
 * for these days" while the calendar drew bars for them.
 */
export function OperatingHoursTable({ store }: OperatingHoursTableProps) {
  const hours = normaliseOperatingHours(store.operating_hours);

  // The schema types operating_hours as an open-ended object, so an unfamiliar
  // shape is expected rather than exceptional — show the payload as-is, above
  // the days, which fall back to the default window.
  const unrecognised = hours.kind === "unrecognised" ? hours.raw : null;
  const extras = hours.kind === "parsed" ? hours.extras : [];

  return (
    <Stack gap="xs">
      {unrecognised !== null ? (
        <>
          <Alert color="yellow" title="Unrecognised format">
            <Text size="sm">
              These per-day hours are not in a shape this app knows how to lay
              out, so every day below falls back to the store&apos;s default
              window. The raw value from the API follows.
            </Text>
          </Alert>
          <Code block>{JSON.stringify(unrecognised, null, 2)}</Code>
        </>
      ) : null}

      <Table striped withTableBorder>
        <Table.Tbody>
          {DAY_ORDER.map((day) => (
            <DayRow
              key={day}
              day={day}
              schedule={storeDaySchedule(store, day)}
            />
          ))}
          {/* Non-weekday entries such as `holiday`, which most stores carry. */}
          {extras.map(({ key, windows }) => (
            <Table.Tr key={key}>
              <Table.Td w={130}>
                <Text size="sm" c="dimmed">
                  {dayLabel(key)}
                </Text>
              </Table.Td>
              <Table.Td>
                {windows.length === 0 ? (
                  <Badge color="gray" variant="light" size="sm">
                    Not set
                  </Badge>
                ) : (
                  <Stack gap={2}>
                    {windows.map((window, index) => (
                      <Text key={index} size="sm" ff="monospace">
                        {fmt.hourRange(window.open, window.close)}
                      </Text>
                    ))}
                  </Stack>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function DayRow({ day, schedule }: { day: string; schedule: DaySchedule }) {
  // Three ways a day reads: shut, no usable times anywhere, or its windows.
  let hours: ReactNode;
  if (schedule.kind === "closed") {
    hours = (
      <Badge color="gray" variant="light" size="sm">
        Closed
      </Badge>
    );
  } else if (schedule.kind === "unknown") {
    hours = (
      <Text size="sm" c="dimmed">
        Hours unknown
      </Text>
    );
  } else {
    hours = (
      <Stack gap={2}>
        {schedule.segments.map((segment, index) => (
          <Text key={index} size="sm" ff="monospace">
            {formatMinutes(segment.startMin)} – {formatMinutes(segment.endMin)}
          </Text>
        ))}
      </Stack>
    );
  }

  return (
    <Table.Tr>
      <Table.Td w={130}>
        <Text size="sm">{dayLabel(day)}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          {hours}
          {schedule.source === "default" && schedule.kind !== "unknown" ? (
            <Badge color="gray" variant="outline" size="xs">
              default window
            </Badge>
          ) : null}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
